import {StatusColor} from '../StatusLabel/StatusLabel';
import {
    ORDER_STATUS_CANCELED,
    TEticketData,
    TEticketSubscriptionEvent,
    TEticketSubscriptionEventState,
    TEticketSubscriptionStatus,
    TEticketTicket
} from '../../../redux/eTicket/eTicket.type';
import {TMemberSubscription} from './memberTicket.type';
import {getTs, toUnixSec} from '../../../library/dates';

export const subscriptionStatusConfig: Record<TEticketSubscriptionStatus, {color: StatusColor; textKey: string}> = {
    active: {color: 'brand', textKey: 'subscriptionStatusActive'},
    expired: {color: 'past', textKey: 'subscriptionStatusExpired'},
    redeemed: {color: 'scanned', textKey: 'subscriptionStatusRedeemed'},
    canceled: {color: 'canceled', textKey: 'subscriptionStatusCanceled'}
};

export const subscriptionEventStateConfig: Record<TEticketSubscriptionEventState, {textKey: string; available: boolean}> = {
    active: {textKey: 'eventStateActive', available: true},
    soldOut: {textKey: 'eventStateSoldOut', available: false},
    inactive: {textKey: 'eventStateInactive', available: false},
    passed: {textKey: 'eventStatePassed', available: false}
};

/* The state is composed here rather than returned as one field: the API hands back the facts
   (has the show passed, is it open for purchase, is there stock left) and only one of them can be
   shown at a time. A show that never opened reads "unavailable" whether or not it has stock. */
export function getSubscriptionEventState(event: TEticketSubscriptionEvent): TEticketSubscriptionEventState {
    const end = toUnixSec(event.timeEnd ?? event.timeStart);
    if(!Number.isNaN(end) && getTs() >= end) {
        return 'passed';
    }
    if(event.active === 0) {
        return 'inactive';
    }
    return event.soldOut ? 'soldOut' : 'active';
}

/* The plan's usage deadline is what the subscription expires by. The season event's own end
   is only a fallback, for a plan that carries no deadline of its own. */
function getUsageEnd(ticket: TEticketTicket, data: TEticketData): number {
    const deadline = toUnixSec(ticket.subscription.plan?.usageDeadline);
    return Number.isNaN(deadline) ? toUnixSec(data.event?.timeEnd) : deadline;
}

/* A subscription stops being active when it is canceled, when all credits are used,
   or once the season's usage period has passed */
function getSubscriptionStatus(ticket: TEticketTicket, data: TEticketData): TEticketSubscriptionStatus {
    if(ticket.canceled || data.order?.status === ORDER_STATUS_CANCELED) {
        return 'canceled';
    }
    if(ticket.subscription.credits.remaining <= 0) {
        return 'redeemed';
    }
    const usageEnd = getUsageEnd(ticket, data);
    return !Number.isNaN(usageEnd) && getTs() >= usageEnd ? 'expired' : 'active';
}

function toOptionalUnix(value?: string | number | null): number | undefined {
    const ts = toUnixSec(value);
    return Number.isNaN(ts) ? undefined : ts;
}

/* Maps a subscription ticket returned by the API to the card's view model */
export function toMemberSubscription(ticket: TEticketTicket, data: TEticketData): TMemberSubscription {
    const {plan, credits, subscribersCount, priceTitle, redeemed, tickets} = ticket.subscription;

    return {
        qid: ticket.qid,
        title: ticket.title ?? '',
        season: data.event?.title,
        seasonStart: toOptionalUnix(data.event?.timeStart),
        seasonEnd: toOptionalUnix(data.event?.timeEnd),
        planType: plan?.planType,
        priceTitle,
        color: ticket.color,
        image: plan?.image,
        status: getSubscriptionStatus(ticket, data),
        totalCredits: credits?.total ?? 0,
        usedCredits: credits?.used ?? 0,
        subscribersCount,
        customerName: ticket.inviterName ?? [data.order?.nameFirst, data.order?.nameLast].filter(Boolean).join(' '),
        description: plan?.description,
        redeemed,
        tickets
    };
}

/* The venues the subscription events take place in. The navigate action is offered only
   when they all share one venue. */
export const getSubscriptionVenues = (tickets?: TEticketTicket[]): string[] => {
    const venues = (tickets ?? []).flatMap(ticket => (ticket.subscription?.events ?? []).map(({location}) => location).filter(Boolean));
    return Array.from(new Set(venues));
};

/* The sheet holds what the producer wrote about the series. With nothing written the button is
   hidden rather than disabled — the series-type badge alone is the empty popup it exists to prevent. */
export const hasSubscriptionInfo = (subscription: TMemberSubscription): boolean =>
    Boolean(subscription.description?.trim() || subscription.image);
